/**
 * Manual time entry — `POST /api/v1/time-entries/manual`.
 *
 * `openapi.yaml` calls this "the one endpoint where a number enters this service on
 * a human's say-so", and the form is shaped by what that endpoint refuses rather
 * than by what it accepts:
 *
 *  - **It does not grey out old dates.** The window is
 *    `wp_policies.correction_max_days_back` (default 7) and **no session endpoint
 *    publishes it**. The form submits; a `409 WORKFORCE_REASON_WINDOW_EXPIRED`
 *    carries `details.earliest_allowed_at`, and *that* is rendered — in the company
 *    timezone — as the answer. See `web/shapes/time-tracking.md` §9. A client-side
 *    copy of a policy the client cannot read would hide days the server accepts and
 *    promise days it refuses, and would be a second authorisation path.
 *  - **It promises nothing about approval.** `manual_entry_requires_approval` is
 *    snapshotted onto the row when it is filed; the created entry's own `status` and
 *    `approval_required` say what happened, and the form reports them back.
 *  - **It sends no `employee_id` and no `source`.** Neither is a field on the
 *    request: an entry is always written by the person it is about, and the service
 *    stamps `manual` so that a client cannot label hand-typed time as observed.
 *
 * The wall-clock inputs are resolved in the **company** timezone by
 * `wallClockToInstant`, not the browser's, and the resolved instants are rendered
 * back before submission so a person can see which interval they are claiming.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatInstant, wallClockToInstant } from '../../i18n/datetime';
import { MANUAL_TIME_ENTRY_PATH } from '../../api/paths';
import type { ManualTimeEntryRequest, TimeEntry } from '../../api/types';
import { conflictingEntryId, earliestAllowedAt, useApiMutation } from '../../api/useApiMutation';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';
import { SourceBadge, TimerStateBadge, ValidationBadge } from '../../timer/badges';
import { useTimer } from '../../timer/TimerProvider';
import { ErrorCodeLine } from '../../states/ErrorCodeLine';

/** `note` is `minLength: 3` on the wire, and `wp_tte_manual_ck` refuses a null one. */
const MIN_NOTE = 3;

export function ManualEntryForm({ timezone }: { readonly timezone: string }): ReactNode {
  const { t, locale } = useI18n();
  const { reload } = useTimer();
  const [open, setOpen] = useState(false);
  const [taskId, setTaskId] = useState('');
  const [startWall, setStartWall] = useState('');
  const [endWall, setEndWall] = useState('');
  const [note, setNote] = useState('');

  const mutation = useApiMutation<ManualTimeEntryRequest, TimeEntry>(MANUAL_TIME_ENTRY_PATH);

  // See `CorrectionForm` — the refusal is where `earliest_allowed_at` appears, so
  // the outcome is where focus belongs once the request has settled.
  const outcomeRef = useRef<HTMLDivElement | null>(null);
  const settled = mutation.status === 'ok' || mutation.status === 'error';
  useEffect(() => {
    if (settled) outcomeRef.current?.focus();
  }, [settled]);

  const startedAt = startWall === '' ? null : wallClockToInstant(startWall, timezone);
  const endedAt = endWall === '' ? null : wallClockToInstant(endWall, timezone);
  const ready =
    taskId.trim() !== '' &&
    startedAt !== null &&
    endedAt !== null &&
    note.trim().length >= MIN_NOTE;

  const windowBoundary = mutation.error === null ? null : earliestAllowedAt(mutation.error);
  const blockedBy = mutation.error === null ? null : conflictingEntryId(mutation.error);

  return (
    <section className="wp-section" aria-labelledby="manual-heading">
      <h2 className="wp-section__title" id="manual-heading">
        {t('manual.heading')}
      </h2>
      <p className="wp-small wp-muted">{t('manual.intro')}</p>

      <button
        type="button"
        className="wp-button"
        aria-expanded={open}
        onClick={() => {
          setOpen((value) => !value);
        }}
        data-testid="manual-toggle"
      >
        {open ? t('manual.close') : t('manual.open')}
      </button>

      {!open ? null : (
        <form
          className="wp-stack"
          data-testid="manual-form"
          onSubmit={(event) => {
            event.preventDefault();
            // `ready` narrows both instants — see `CorrectionForm`.
            if (!ready) return;
            void mutation
              .submit({
                achau_task_id: taskId.trim(),
                started_at: startedAt,
                ended_at: endedAt,
                note: note.trim(),
              })
              .then(() => {
                // A manual entry can land `pending_approval` or `stopped`; neither is a
                // live timer, so the held entry does not change. The re-read is still
                // right: it is the one place that would notice if it had.
                reload();
              });
          }}
        >
          <div className="wp-field">
            <label htmlFor="manual-task">{t('manual.task')}</label>
            <input
              id="manual-task"
              value={taskId}
              onChange={(event) => {
                setTaskId(event.target.value);
              }}
              data-testid="manual-task"
            />
          </div>
          <div className="wp-field">
            <label htmlFor="manual-start">{t('manual.start')}</label>
            <input
              id="manual-start"
              type="datetime-local"
              value={startWall}
              onChange={(event) => {
                setStartWall(event.target.value);
              }}
              data-testid="manual-start"
            />
          </div>
          <div className="wp-field">
            <label htmlFor="manual-end">{t('manual.end')}</label>
            <input
              id="manual-end"
              type="datetime-local"
              value={endWall}
              onChange={(event) => {
                setEndWall(event.target.value);
              }}
              data-testid="manual-end"
            />
          </div>
          <div className="wp-field">
            <label htmlFor="manual-note">{t('manual.note')}</label>
            <input
              id="manual-note"
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
              data-testid="manual-note"
            />
            <p className="wp-small wp-muted">{t('manual.note.hint', { min: MIN_NOTE })}</p>
          </div>

          {startedAt === null || endedAt === null ? null : (
            <p className="wp-small wp-muted" data-testid="manual-resolved">
              {t('manual.resolved', {
                from: formatInstant(startedAt, locale, timezone),
                to: formatInstant(endedAt, locale, timezone),
              })}
            </p>
          )}

          <button
            type="submit"
            className="wp-button wp-button--primary"
            disabled={!ready || mutation.status === 'pending'}
            data-testid="manual-submit"
          >
            {mutation.status === 'pending' ? t('manual.submitting') : t('manual.submit')}
          </button>
        </form>
      )}

      <div ref={outcomeRef} tabIndex={-1} data-testid="manual-outcome">
        {mutation.status === 'ok' && mutation.data !== null ? (
          <StatusNote severity="low" title={t('manual.recorded.title')} testId="manual-recorded">
            <p>
              {mutation.data.approval_required === true
                ? t('manual.recorded.pending')
                : t('manual.recorded.counted')}
            </p>
            <p className="wp-flags">
              <TimerStateBadge status={mutation.data.status} testId="manual-recorded-state" />
              <SourceBadge source={mutation.data.source} testId="manual-recorded-source" />
              <ValidationBadge
                state={mutation.data.task_validation_state}
                testId="manual-recorded-validation"
              />
            </p>
          </StatusNote>
        ) : null}

        {mutation.error === null ? null : windowBoundary !== null ? (
          // The one screen on which a tenant's `correction_max_days_back` becomes
          // visible at all. `role="alert"` because it answers something the person
          // just did.
          <StatusNote
            severity="medium"
            role="alert"
            title={t('manual.window.title')}
            testId="manual-window-expired"
          >
            <p>
              {t('manual.window.body', {
                earliest: formatInstant(windowBoundary, locale, timezone),
              })}
            </p>
            <ErrorCodeLine
              code={mutation.error.code}
              correlationId={mutation.error.correlationId}
            />
          </StatusNote>
        ) : blockedBy !== null ? (
          <StatusNote
            severity="medium"
            role="alert"
            title={t('manual.overlap.title')}
            testId="manual-overlap"
          >
            <p>{t('manual.overlap.body', { id: blockedBy })}</p>
          </StatusNote>
        ) : (
          <ApiErrorNote
            error={mutation.error}
            subject={t('manual.subject')}
            testId="manual-error"
          />
        )}
      </div>
    </section>
  );
}
